import { ChangeDetectionStrategy, Component, computed, effect, inject, linkedSignal, signal } from '@angular/core';
import { FormField, form, min, required, validate } from '@angular/forms/signals';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';

import { finalize, from, map, mergeMap, tap } from 'rxjs';

import { Agreement, AgreementStatuses, BillingCycles, Document, ServiceTypes } from '../schemas';
import { AgreementService, ClientService, Mail, Messenger, ProductService, UserService } from '../service';
import { Autocomplete } from './autocomplete';

export interface AgreementFormData {
  title?: string;
  agreement?: Agreement;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormField,
    MatDialogModule,
    MatCheckboxModule,
    MatButtonModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatTooltipModule,
    MatIconModule,
    MatAutocompleteModule,
    MatProgressBarModule,
    MatListModule,
    Autocomplete,
  ],

  templateUrl: './agreement-form.html',
  styles: ``,
})
export class AgreementForm {
  data = (inject(MAT_DIALOG_DATA) as AgreementFormData) || {};

  hostDialog = inject(MatDialogRef);

  messenger = inject(Messenger);
  agreementService = inject(AgreementService);
  clientService = inject(ClientService);
  userService = inject(UserService);
  productService = inject(ProductService);
  mailer = inject(Mail);

  pending = signal(false);

  serviceTypes = signal(ServiceTypes);

  clientCodes = this.clientService.codes;
  statuses = signal(AgreementStatuses);
  billingCycles = signal(BillingCycles);

  productOwners = this.userService.names;

  jobNumbers = computed(() =>
    this.form.ClientCode().value()
      ? this.productService
          .products()
          .filter((it) => it.ClientCode === this.form.ClientCode().value())
          .map((it) => it.JobNumber)
          .sort()
      : [],
  );

  commissionables = computed(() =>
    this.clientService
      .clients()
      .filter((it) => it.Commissionable)
      .map((it) => it.ClientCode),
  );

  model = linkedSignal(() => {
    return {
      AgreementNumber: '',
      BillingCycle: '',
      BudgetNote: '',
      ClientCode: '',
      Commissionable: false,
      EndDate: '',
      ExtendedLifetime: false,
      HasSLA: false,
      HostingAmount: 0,
      InvoiceAmount: 1,
      JiraLicence: 0,
      JiraTitle: '',
      JobNumber: '',
      Notes: '',
      ProductOwner: '',
      ServiceAmount: 0,
      ServiceType: '',
      StartDate: '',
      Status: '',
      SupportHours: 0,
      SupportMaintenanceAmount: 0,
      Title: '',
      Documents: [] as Document[],
      ...this.data.agreement,
    } as Agreement;
  });

  form = form(this.model, (schema) => {
    required(schema.BillingCycle);
    required(schema.ClientCode);
    required(schema.EndDate);
    required(schema.JobNumber);
    required(schema.ServiceType);
    required(schema.StartDate);
    required(schema.Status);
    required(schema.SupportHours);

    validate(schema.JobNumber, ({ value }) => {
      if (!this.jobNumbers().includes(value())) {
        return { kind: 'choice', message: 'Product must belong to client' };
      }
      return null;
    });

    validate(schema.SupportHours, ({ valueOf, value }) => {
      if (valueOf(schema.HasSLA) && value() < 1) {
        return { kind: 'min', message: 'Must be a positive number' };
      }
      return null;
    });

    min(schema.SupportHours, 0, {});

    required(schema.Title);

    required(schema.InvoiceAmount);
    min(schema.InvoiceAmount, 1);

    required(schema.HostingAmount);
    min(schema.HostingAmount, 0);

    required(schema.ServiceAmount);
    min(schema.ServiceAmount, 0);

    required(schema.SupportMaintenanceAmount);
    min(schema.SupportMaintenanceAmount, 0);

    required(schema.JiraLicence);
    min(schema.JiraLicence, 0);
  });

  constructor() {
    effect(() => {
      const code = this.form.ClientCode().value();
      this.form.Commissionable().value.set(this.commissionables().includes(code));
    });
  }

  save() {
    const data = this.form().value();
    const existing = this.data.agreement;
    this.pending.set(true);
    this.agreementService
      .save(data, !!existing)
      .pipe(finalize(() => this.pending.set(false)))
      .subscribe({
        next: () => {
          if (data.Status === 'Expired' && this.data.agreement?.Status !== 'Expired') {
            this.mailer.sendExpiredAgreement(data);
          }
          this.hostDialog.close(data);
        },
        error: this.messenger.handleHttpError,
      });
  }

  uploadDocuments(files: FileList | null) {
    if (!files) {
      return;
    }
    this.pending.set(true);
    from(files)
      .pipe(
        mergeMap((file) =>
          this.agreementService.prepareDocumentUpload(file.name, file.type).pipe(
            tap((prepared) => {
              fetch(prepared.url, {
                method: 'PUT',
                body: file,
                mode: 'cors',
                headers: {
                  'Content-Type': file.type,
                },
              });
            }),
            map((prepared) => ({ DocumentId: prepared.key, DocumentName: file.name })),
          ),
        ),
        finalize(() => this.pending.set(false)),
      )
      .subscribe({
        next: (freshDoc) => this.form.Documents().value.update((documents) => [...documents, freshDoc]),
      });
  }

  removeDocument(document: Document): void {
    this.form.Documents().value.update((documents) => documents.filter((doc) => doc !== document));
  }

  downloadDocument(document: Document): void {
    this.form
      .Documents()
      .value()
      .forEach((doc) => doc === document && this.agreementService.downloadDocument(doc.DocumentId));
  }
}
